import React from "react";
import Head from "next/head";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import Custom404 from "pages/404";
import { CamelotLayout } from "components/dex/camelot/camelot-layout/camelot-layout";

enum ContestLink {
  Camelot = "camelot",
}

const contestIdByName: Record<ContestLink, number> = {
  [ContestLink.Camelot]: 7,
};

const faviconByContestId: Record<number, string> = {
  [contestIdByName.camelot]: "/contest/meta/camelot/favicon.ico",
};

const ogImageByContestId: Record<number, string> = {
  [contestIdByName.camelot]: "https://camelot.marginly.com/meta/camelot/og.png",
};

const renderComponentByContestId = (contestId: number | null) => {
  if (!contestId) return <Custom404 />;

  const componentByContestId: Record<number, React.ReactNode> = {
    [contestIdByName.camelot]: <CamelotLayout contestId={contestId} />,
  };

  const dexContest = componentByContestId[contestId];
  if (!dexContest) return <Custom404 />;
  return dexContest;
};

export async function getServerSideProps({
  params,
}: GetServerSidePropsContext) {
  const contest = params?.contest;
  const contestId = contestIdByName[contest as ContestLink];
  if (typeof contest !== "string" || !contestId)
    return { props: { contestId: null } };
  return {
    props: { contestId: Number(contestId) },
  };
}

const HomePage: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ contestId }) => {
  const favicon = contestId && faviconByContestId[contestId];
  const ogImage = contestId && ogImageByContestId[contestId];
  return (
    <>
      <Head>
        <title>Marginly</title>
        <meta
          name="description"
          content="Marginly enables leverage and derivatives trading where they never existed before. Trade spot with up to 20x leverage. Provide liquidity and earn yield. Hedge your portfolio. Utilize non-trivial option strategies. Earn in range when price doesn't move. "
        />
        <meta property="og:url" content="https://marginly.com/" />

        <meta property="og:type" content="website" />
        {favicon && <link rel="icon" href={favicon} sizes="any" />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta
          property="og:title"
          content="A new EVM-based layer for decentralized derivatives."
        />
        <meta
          property="og:description"
          content="Marginly enables leverage and derivatives trading where they never existed before. Trade spot with up to 20x leverage. Provide liquidity and earn yield. Hedge your portfolio. Utilize non-trivial option strategies. Earn in range when price doesn't move. "
        />
        <meta property="og:image" content="/og.png" />

        <meta
          name="keywords"
          content="Trade, swap, defi, leverage, long-tail assets, derivatives, amm, dex"
        />

        <meta name="theme-color" content="#ffffff" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:domain" content="marginly.com" />
        <meta name="twitter:url" content="https://marginly.com/" />
        <meta
          name="twitter:title"
          content="A new EVM-based layer for decentralized derivatives."
        />
        <meta
          name="twitter:description"
          content="Marginly enables leverage and derivatives trading where they never existed before. Trade spot with up to 20x leverage. Provide liquidity and earn yield. Hedge your portfolio. Utilize non-trivial option strategies. Earn in range when price doesn't move. "
        />
        <meta name="twitter:image" content="/og.png" />
      </Head>
      {renderComponentByContestId(contestId)}
    </>
  );
};

export default HomePage;
